import React, { useState } from 'react';
import { MapPinIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { updateVenueAddress } from '../Services/VenueService.ts';
import CountryCitySelect from './CountryCitySelect';

/**
 * VenueAddressForm
 *
 * Props:
 *  - venueKey  {string}  required – the venue short code
 *  - initial   {object}  optional – pre-populate fields from existing venue data
 *      { line1, line2, line3, city, county, postCode }
 *  - onSaved   {fn}      optional – called with the updated address after a successful save
 */
const VenueAddressForm = ({ venueKey, initial = {}, onSaved }) => {
    const [form, setForm] = useState({
        line1:    initial.line1    || '',
        line2:    initial.line2    || '',
        line3:    initial.line3    || '',
        city:     initial.city     || '',
        county:   initial.county   || '',
        country:  initial.country  || 'United Kingdom',
        postCode: initial.postCode || '',
    });

    const [saving, setSaving]   = useState(false);
    const [status, setStatus]   = useState(null); // 'success' | 'error'
    const [message, setMessage] = useState('');

    const fieldClass =
        'w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg ' +
        'text-white placeholder-gray-500 text-sm ' +
        'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

    const labelClass = 'block text-sm font-medium text-gray-300 mb-1';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        // Clear any previous status when the user starts editing again
        if (status) setStatus(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.line1.trim()) {
            setStatus('error');
            setMessage('Address Line 1 is required.');
            return;
        }
        if (!form.city.trim()) {
            setStatus('error');
            setMessage('City is required.');
            return;
        }
        if (!form.postCode.trim()) {
            setStatus('error');
            setMessage('Postcode is required.');
            return;
        }

        setSaving(true);
        setStatus(null);
        setMessage('');

        try {
            await updateVenueAddress(venueKey, {
                line1:    form.line1.trim(),
                line2:    form.line2.trim(),
                line3:    form.line3.trim(),
                city:     form.city.trim(),
                county:   form.county.trim(),
                postCode: form.postCode.trim(),
            });

            setStatus('success');
            setMessage('Address updated successfully.');
            if (onSaved) onSaved({ ...form });
        } catch (err) {
            setStatus('error');
            setMessage(err.message || 'Failed to save address. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
                <MapPinIcon className="h-6 w-6 text-emerald-400 flex-shrink-0" />
                <h2 className="text-xl font-semibold text-white">Venue Address</h2>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {/* Line 1 */}
                <div>
                    <label className={labelClass} htmlFor="va-line1">
                        Address Line 1 <span className="text-red-400">*</span>
                    </label>
                    <input
                        id="va-line1"
                        name="line1"
                        type="text"
                        autoComplete="address-line1"
                        value={form.line1}
                        onChange={handleChange}
                        placeholder="Building number and street name"
                        className={fieldClass}
                        maxLength={200}
                    />
                </div>

                {/* Line 2 */}
                <div>
                    <label className={labelClass} htmlFor="va-line2">
                        Address Line 2 <span className="text-gray-500 font-normal">(optional)</span>
                    </label>
                    <input
                        id="va-line2"
                        name="line2"
                        type="text"
                        autoComplete="address-line2"
                        value={form.line2}
                        onChange={handleChange}
                        placeholder="Suite, unit, floor, etc."
                        className={fieldClass}
                        maxLength={200}
                    />
                </div>

                {/* Line 3 */}
                <div>
                    <label className={labelClass} htmlFor="va-line3">
                        Address Line 3 <span className="text-gray-500 font-normal">(optional)</span>
                    </label>
                    <input
                        id="va-line3"
                        name="line3"
                        type="text"
                        value={form.line3}
                        onChange={handleChange}
                        placeholder="District or area"
                        className={fieldClass}
                        maxLength={200}
                    />
                </div>

                {/* Country + City row */}
                <CountryCitySelect
                    country={form.country}
                    city={form.city}
                    onCountryChange={val => {
                        setForm(prev => ({ ...prev, country: val }));
                        if (status) setStatus(null);
                    }}
                    onCityChange={val => {
                        setForm(prev => ({ ...prev, city: val }));
                        if (status) setStatus(null);
                    }}
                    cityLabel="City / Town"
                    cityRequired
                    fieldClass={fieldClass}
                    labelClass={labelClass}
                />

                {/* County */}
                <div>
                    <label className={labelClass} htmlFor="va-county">
                        County <span className="text-gray-500 font-normal">(optional)</span>
                    </label>
                    <input
                        id="va-county"
                        name="county"
                        type="text"
                        value={form.county}
                        onChange={handleChange}
                        placeholder="e.g. Greater London"
                        className={fieldClass}
                        maxLength={100}
                    />
                </div>

                {/* Postcode */}
                <div className="sm:w-48">
                    <label className={labelClass} htmlFor="va-postcode">
                        Postcode <span className="text-red-400">*</span>
                    </label>
                    <input
                        id="va-postcode"
                        name="postCode"
                        type="text"
                        autoComplete="postal-code"
                        value={form.postCode}
                        onChange={handleChange}
                        placeholder="e.g. SW1A 1AA"
                        className={`${fieldClass} uppercase`}
                        maxLength={10}
                    />
                </div>

                {/* Status banner */}
                {status === 'success' && (
                    <div className="flex items-center gap-2 bg-emerald-900/30 border border-emerald-700 text-emerald-300 rounded-lg px-4 py-3 text-sm">
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                        {message}
                    </div>
                )}
                {status === 'error' && (
                    <div className="flex items-center gap-2 bg-red-900/30 border border-red-700 text-red-300 rounded-lg px-4 py-3 text-sm">
                        <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                        {message}
                    </div>
                )}

                {/* Submit */}
                <div className="flex justify-end pt-2">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg font-medium transition-colors text-sm"
                    >
                        {saving ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                Saving…
                            </>
                        ) : (
                            <>
                                <MapPinIcon className="h-4 w-4" />
                                Save Address
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default VenueAddressForm;

